import {
  Directive,
  ElementRef,
  Input,
  OnInit,
  AfterViewInit,
  ChangeDetectorRef,
} from "@angular/core";
import intlTelInput from "intl-tel-input";
import { FormGroupDirective } from "@angular/forms";
import { InstituicaoProvider } from "src/app/services/instituicao/instituicao";

/*
  _/﹋\_
  (҂`_´) - Exemplo de uso do NgxMaskDirective e PhoneIntlDirective para formatação de inputs
  <;︻╦╤─ ҉ - - - - - - - - - - - - -

  import { NgxMaskDirective } from 'ngx-mask';
  <input
    type="tel"
    formControlName="telCelular"
    [ddiTarget]="pessoa.contato?.ddiTelCelular || '+55'"   <!-- DDI padrão Brasil -->
    [maskMap]="maskMap"                                   <!-- Mapeamento de máscaras -->
    [mask]="maskMap.get('phoneCelular')"                  <!-- Máscara dinâmica do celular -->
    phoneIntl="phoneCelular"                              <!-- Identificador da máscara -->
    [showFlag]="false"                                    <!-- Ocultar bandeira do país -->
    placeholder="{{ 'Digite seu celular' | translate }}"  <!-- Placeholder traduzido -->
  />
*/

@Directive({
  selector: "[phoneIntl]",
  standalone: true,
})
export class PhoneIntlDirective implements AfterViewInit {
  @Input("phoneIntl") key!: string;
  @Input() ddiTarget!: string;
  @Input() maskMap!: Map<string, string>;
  @Input() showFlag: boolean = true;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private cdr: ChangeDetectorRef,
    private formGroupDir: FormGroupDirective,
    public instituicaoProvider: InstituicaoProvider
  ) { }

  ngAfterViewInit() {
    const inputElement = this.el.nativeElement as HTMLInputElement;
    let ddi = "+55";
    const form = this.formGroupDir.control;

    if (this.ddiTarget && form.get(this.ddiTarget)?.value) {
      ddi = form.get(this.ddiTarget)?.value;
    } else {
      form.get(this.ddiTarget)?.setValue(ddi);
    }

    const defaultIso2 = this.instituicaoProvider.objDddi()[ddi];
    let lastIso2 = defaultIso2;
    let lastIsoMaster = defaultIso2;

    const applyPhoneMask = (iso2: string) => {
      const objectMask = this.getPhoneMasks();
      const selectedMask = objectMask[iso2] || "(00)0000-0000";
      this.maskMap.set(this.key, selectedMask);
      this.cdr.detectChanges();
    };

    applyPhoneMask(lastIso2);
    if (!this.showFlag) return;

    const iti = intlTelInput(inputElement, {
      countryOrder: ["Us", "Gb", "Br", "Es", "Sa", "Ca"],
      separateDialCode: true,
      initialCountry: lastIso2,
      formatOnDisplay: true,
      formatAsYouType: true,
      countrySearch: false,
    });

    inputElement.addEventListener("countrychange", () => {
      const selectedCountryData = iti.getSelectedCountryData();
      if (selectedCountryData) {
        const ddi = "+" + selectedCountryData.dialCode;
        const iso2 = selectedCountryData.iso2?.toLowerCase() || lastIso2;
        this.formGroupDir.control.get(this.ddiTarget)?.setValue(ddi);
        lastIso2 = iso2;
        lastIsoMaster = iso2;
        applyPhoneMask(iso2);
      }
    });

    inputElement.addEventListener("input", (event: any) => {
      if (event.inputType === "deleteContentBackward") {
        lastIso2 = lastIsoMaster;
      }
      applyPhoneMask(lastIso2);
    });
  }

  getPhoneMasks(): { [key: string]: string } {
    return {
      ac: "0000",
      ad: "000-000",
      ae: "00-000-0000",
      af: "00-000-0000",
      ag: "(000)000-0000",
      ai: "(000)000-0000",
      al: "(000)000-000",
      am: "00-000-000",
      an: "0000-0000",
      ao: "(000)000-000",
      aq: "000-000",
      ar: "(000)000-0000",
      as: "(000)000-0000",
      at: "(000)000-0000",
      au: "0-0000-0000",
      aw: "000-0000",
      az: "00-000-00-00",
      ba: "00-00000",
      bb: "(000)000-0000",
      bd: "00-000-000",
      be: "(000)000-000",
      bf: "00-00-0000",
      bg: "(000)000-000",
      bh: "0000-0000",
      bi: "00-00-0000",
      bj: "00-00-0000",
      bm: "(000)000-0000",
      bn: "000-0000",
      bo: "0-000-0000",
      br: "(00)00000-0000",
      bs: "(000)000-0000",
      bt: "00-000-000",
      bw: "00-000-000",
      by: "(00)000-00-00",
      bz: "000-0000",
      ca: "(000)000-0000",
      cd: "(000)000-000",
      cf: "00-00-0000",
      cg: "00-000-0000",
      ch: "00-000-0000",
      ci: "00-000-000",
      ck: "00-000",
      cl: "0-0000-0000",
      cm: "0000-0000",
      cn: "(000)0000-0000",
      co: "(000)000-0000",
      cr: "0000-0000",
      cu: "0-000-0000",
      cv: "(000)00-00",
      cw: "000-0000",
      cy: "00-000-000",
      cz: "(000)000-000",
      de: "(0000)000-0000",
      dj: "00-00-00-00-00",
      dk: "00-00-00-00-00",
      dm: "(000)000-0000",
      do: "(000)000-0000",
      dz: "00-000-0000",
      ec: "00-000-0000",
      ee: "0000-0000",
      eg: "(000)000-0000",
      er: "0-000-000",
      es: "(000)000-000",
      et: "00-000-0000",
      fi: "(000)000-00-00",
      fj: "00-00000",
      fk: "00000",
      fm: "000-0000",
      fo: "000-000",
      fr: "(000)000-000",
      ga: "0-00-00-00-00",
      gd: "(000)000-0000",
      ge: "(000)000-000",
      gf: "00000-0000",
      gh: "(000)000-000",
      gi: "000-00000",
      gl: "00-00-00-00",
      gm: "(000)00-00",
      gn: "00-000-000",
      gq: "00-000-0000",
      gr: "(000)000-0000",
      gt: "0-000-0000",
      gu: "(000)000-0000",
      gw: "0-000000",
      gy: "000-0000",
      hk: "0000-0000",
      hn: "0000-0000",
      hr: "00-000-000",
      ht: "00-00-0000",
      hu: "(000)000-000",
      id: "(000)000-00-000",
      ie: "(000)000-000",
      il: "00-000-0000",
      in: "(0000)000-000",
      io: "000-0000",
      iq: "(000)000-0000",
      ir: "(000)000-0000",
      is: "000-0000",
      it: "(000)0000-000",
      jm: "(000)000-0000",
      jo: "0-0000-0000",
      jp: "(000)000-000",
      ke: "000-000-0000",
      kg: "(000)000-000",
      kh: "00-000-000",
      ki: "00-000",
      km: "00-00000",
      kn: "(000)000-0000",
      kp: "000-0000-000",
      kr: "00-000-0000",
      kw: "0000-0000",
      ky: "(000)000-0000",
      kz: "(000)000-00-00",
      la: "(0000)000-000",
      lb: "00-000-000",
      lc: "(000)000-0000",
      li: "(000)000-0000",
      lk: "00-000-0000",
      lr: "00-000-000",
      ls: "0-000-0000",
      lt: "(000)00-000",
      lu: "(000)000-000",
      lv: "00-000-000",
      ly: "00-000-000",
      ma: "00-0000-000",
      mc: "(000)000-000",
      md: "0000-0000",
      me: "00-000-000",
      mg: "00-00-00000",
      mh: "000-0000",
      mk: "00-000-000",
      ml: "00-00-0000",
      mm: "00-000-000",
      mn: "00-00-0000",
      mo: "0000-0000",
      mp: "(000)000-0000",
      mq: "(000)00-00-00",
      mr: "00-00-0000",
      ms: "(000)000-0000",
      mt: "0000-0000",
      mu: "000-0000",
      mv: "000-0000",
      mw: "0-0000-0000",
      mx: "(000)000-0000",
      my: "(000)000-000",
      mz: "00-000-000",
      na: "00-000-0000",
      nc: "00-0000",
      ne: "00-00-0000",
      nf: "000-000",
      ng: "(000)000-0000",
      ni: "0000-0000",
      nl: "00-000-0000",
      no: "(000)00-000",
      np: "00-000-000",
      nr: "000-0000",
      nu: "0000",
      nz: "(000)000-000",
      om: "00-000-000",
      pa: "000-0000",
      pe: "(000)000-000",
      pf: "00-00-00",
      pg: "(000)00-000",
      ph: "(000)000-0000",
      pk: "(000)000-0000",
      pl: "(000)000-000",
      ps: "00-000-0000",
      pt: "00-000-0000",
      pw: "000-0000",
      py: "(000)000-000",
      qa: "0000-0000",
      re: "00000-0000",
      ro: "00-000-0000",
      rs: "00-000-0000",
      ru: "(000)000-00-00",
      rw: "(000)000-000",
      sa: "(0000) 000 000",
      sb: "000-0000",
      sc: "0-000-000",
      sd: "00-000-0000",
      se: "00-000-0000",
      sg: "0000-0000",
      sh: "0000",
      si: "00-000-000",
      sk: "(000)000-000",
      sl: "00-000000",
      sm: "0000-000000",
      sn: "00-000-0000",
      so: "00-000-000",
      sr: "000-0000",
      ss: "00-000-0000",
      st: "00-00000",
      sv: "00-00-0000",
      sx: "(000)000-0000",
      sy: "00-0000-000",
      sz: "00-00-0000",
      tc: "(000)000-0000",
      td: "00-00-00-00",
      tg: "00-000-000",
      th: "00-000-0000",
      tj: "00-000-0000",
      tk: "0000",
      tl: "000-0000",
      tm: "0-000-0000",
      tn: "00-000-000",
      to: "00000",
      tr: "(000)000-0000",
      tt: "(000)000-0000",
      tv: "000000",
      tw: "0-0000-0000",
      tz: "00-000-0000",
      ua: "(00)000-00-00",
      ug: "(000)000-000",
      gb: "00-0000-000000",
      us: "(000)000-0000",
      uy: "0-000-00-00",
      uz: "00-000-0000",
      va: "0-000-00000",
      vc: "(000)000-0000",
      ve: "(000)000-0000",
      vg: "(000)000-0000",
      vi: "(000)000-0000",
      vn: "(000)0000-000",
      vu: "00-00000",
      wf: "00-0000",
      ws: "00-0000",
      ye: "000-000-000",
      za: "00-000-0000",
      zm: "00-000-0000",
      zw: "0-000000",
    };
  }
}